import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircleadditionIcon, CirclesubtractionIcon } from "../../../../../assets/icons/svgicons";
import { getUserCoinListAction } from "../../../../../features/user/userSlice";
import { toFix } from "../../../../../Helper/helperfunction";
import { STATUS_CODE } from "../../../../../utils/constants";
import { formatPrice } from "../../../../../utils/utils";
import Button from "../../../../common/Button/Button";
import Select from "../../../../common/Formik/Select/Select";
import { toasts } from "../../../../common/Toast/Toast";
import styles from "./MarketPlaceCard.module.scss";

const MarketPlaceCard = ({
  largeTokenIcon, data, handleAddtoCart, inCart, handleViewDetails, imageUrl, handleIncDec, handleGotoCart, premiumFee,
  onClicksell, tokenIcon, tokentitle, tokensubInfo, tokenCoin, pricelabel, pricevalue, changelabel, changevalue,
}) => {
  const dispatch = useDispatch();
  const [coinLists, setCoinList] = useState([]);
  const [isReadMoreVisible, setIsReadMoreVisible] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const spotPremiumPrice = useSelector((state) => state?.users?.spotPremiumPrice);
  const cartItems = useSelector((state) => state?.marketPlace?.cartItems || []);
  const [quantityDecimal, setQuantityDecimal] = useState(0);
  const [minOrderQty, setMinOrderQty] = useState(0);
  const [valueInput, setValueInput] = useState("");
  const tokenLabel = data?.token_id === 1 ? "GSTBL" : "SSTBL";


  const timeOptions = [
    { label: "Order By Qty", value: "WEEK", timeUnit: "day", count: 1, },
    { label: "Order By Value", value: "DAY", timeUnit: "hour", count: 2, },
  ];
  const [filter, setFilter] = useState(timeOptions[0]);
  const descriptionRef = useRef(null);

  useEffect(() => {
    const descriptionElement = descriptionRef.current;
    if (descriptionElement) {
      setIsReadMoreVisible(
        descriptionElement.scrollHeight >
        2 * parseFloat(getComputedStyle(descriptionElement).lineHeight)
      );
    }
  }, [data?.description]);

  const kycStatus = useSelector(
    (state) => state?.persist?.userInfo?.kyc_status
  );

  const handleAddtoCartClick = () => {
    if (kycStatus === "APPROVED") {
      const unitPrice = getUnitPrice();
      const qty = Number(valueInput) / unitPrice;
      const currentMode =
        filter.label === "Order By Qty" ? "QUANTITY" : "VALUE";
      // Check if the product is already in the cart
      const existingCartItem = cartItems.find(
        (item) => item.product_id === data?.id
      );

      if (existingCartItem) {
        if (existingCartItem.selection_mode !== currentMode) {
          toasts.error(
            `Please use the same mode or remove the item from cart first.`
          );
          return;
        }
      }

      handleAddtoCart(filter.label, valueInput, qty);

      // Reset to "Order By Qty" mode after successful add to cart
      // const selectedCoin = coinLists.find(
      //   (coin) => coin.coin_symbol === data?.category
      // );
      // const quantityDecimal = selectedCoin?.quantity_decimal || 0;
      // const minOrderQty = selectedCoin?.min_order_qty || 0;

      // Reset to default mode
      setFilter(timeOptions[0]);

      // Reset quantity to minimum
      // handleIncDec(data?.id, "", minOrderQty, quantityDecimal, minOrderQty);

      // Clear value input
      // setValueInput("");

    } else {
      toasts.error("Please verify your KYC");
    }
  };

  const getRangePrice = (qty, priceChart) => {
    if (!priceChart || priceChart.length === 0) {
      return 0;
    }
    const matchingPrice =
      Array.isArray(priceChart) &&
      priceChart?.find(
        (priceRange) =>
          qty >= priceRange.min_quantity && qty <= priceRange.max_quantity
      );

    if (matchingPrice) {
      const priceWithFee =
        Number(matchingPrice.stripe_payment_price) +
        Number(matchingPrice.fee_value || 0);
      return priceWithFee;
    }

    return 0;
  };

  const fetchCoinOptions = async () => {
    try {
      const response = await dispatch(getUserCoinListAction());
      if (response.data?.status === STATUS_CODE.successful) {
        const newData = response.data?.data?.coinList;
        setCoinList(newData);

        const selectedCoin = newData.find(
          (coin) => coin.coin_symbol === data?.category
        );

        const quantityDecimal = selectedCoin?.quantity_decimal || 0;
        const minOrderQty = selectedCoin?.min_order_qty || 0;
        setQuantityDecimal(quantityDecimal);
        setMinOrderQty(minOrderQty);

        if (
          filter.label === "Order By Value" &&
          data?.quantity !== minOrderQty
        ) {
          handleIncDec(
            data?.id,
            "",
            minOrderQty,
            quantityDecimal,
            minOrderQty,
            minOrderQty * getUnitPrice()
          );
          setValueInput((minOrderQty * getUnitPrice()).toFixed(2));
        }
      }
    } catch (error) {
      console.error("Error fetching coin options:", error);
    }
  };

  const getTotalPrice = (item) => {
    const priceChartPrice = getRangePrice(item?.quantity, data?.productPrices);
    if (item?.token_id === 1) {
      return formatPrice(Number(item?.quantity * spotPremiumPrice?.goldPrice));
    } else {
      return formatPrice(
        Number(item?.quantity * spotPremiumPrice?.silverPrice)
      );
    }
  };

  const getUnitPrice = () => {
    const basePrice =
      data?.token_id === 1
        ? spotPremiumPrice?.goldPrice
        : spotPremiumPrice?.silverPrice;
    return basePrice || 0;
  };

  const getQuantityFromValue = (val) => {
    const unitPrice = getUnitPrice();
    if (!unitPrice) return 0;
    return val / unitPrice;
  };

  const handleInputChange = (productId, e) => {
    const { value } = e.target;

    const selectedCoin = coinLists.find(
      (coin) => coin.coin_symbol === data?.category
    );
    const maxDecimalPlaces = selectedCoin?.quantity_decimal || 2;
    const minOrderQty = selectedCoin?.min_order_qty || 0;

    // For value mode, always limit to 2 decimal places. For quantity mode, use coin's decimal places
    const decimalPlaces =
      filter.label === "Order By Value" ? 2 : maxDecimalPlaces;
    const regex = new RegExp(`^\\d*(\\.\\d{0,${decimalPlaces}})?$`);
    if (!regex.test(value) && value !== "") return;

    if (filter.label === "Order By Qty") {
      handleIncDec(productId, "", value, maxDecimalPlaces, minOrderQty);
    } else {
      setValueInput(value);
    }
  };

  const handleToggleDescription = () => {
    if (isReadMoreVisible) {
      handleViewDetails();
    } else {
      setIsDescriptionExpanded(!isDescriptionExpanded);
    }
  };

  const getMaxQuantityFromProductPrices = () => {
    const maxQuantities = data?.productPrices?.map(
      (price) => price.max_quantity
    );
    return Math.max(...maxQuantities);
  };

  const getCurrentPremiumPrice = () => {
    if (data?.token_id === 1) {
      return formatPrice(spotPremiumPrice?.goldPrice || 0);
    } else {
      return formatPrice(spotPremiumPrice?.silverPrice || 0);
    }
  };


  useEffect(() => {
    fetchCoinOptions();
  }, []);

  useEffect(() => {
    if (filter.label !== "Order By Value") return;

    const selectedCoin = coinLists.find(
      (coin) => coin.coin_symbol === data?.category
    );
    if (!selectedCoin) return;

    const minOrderQty = selectedCoin.min_order_qty || 0;

    if (!valueInput || isNaN(Number(valueInput)) || Number(valueInput) <= 0) {
      const defaultVal = (minOrderQty * getUnitPrice()).toFixed(2);
      setValueInput(defaultVal);
    }
  }, [filter.label]);

  useEffect(() => {
    if (filter.label !== "Order By Value") return;
    const selectedCoin = coinLists.find(
      (coin) => coin.coin_symbol === data?.category
    );
    if (!selectedCoin || !valueInput) return;

    const maxDecimalPlaces = selectedCoin.quantity_decimal || 2;
    const minOrderQty = selectedCoin.min_order_qty || 0;
    const unitPrice = getUnitPrice();
    const numericVal = Number(valueInput);

    if (isNaN(numericVal)) return;

    const timeout = setTimeout(() => {
      const qty = numericVal / unitPrice;
      const multiplier = Math.pow(10, maxDecimalPlaces);

      let finalQty;
      if (numericVal === 0) {
        finalQty = 0;
      } else {
        // const ceilQty = Math.ceil(qty * multiplier) / multiplier;
        // finalQty = Math.max(ceilQty, minOrderQty);
        const flooredQty = Math.floor(qty * multiplier) / multiplier;
        finalQty = Math.max(flooredQty, minOrderQty);
      }

      handleIncDec(
        data?.id,
        "",
        finalQty,
        maxDecimalPlaces,
        minOrderQty,
        numericVal
      );
    }, 300);

    return () => clearTimeout(timeout);
  }, [valueInput]);
  useEffect(() => {
    if (!coinLists.length || !data?.id) return;

    const selectedCoin = coinLists.find(
      (coin) => coin.coin_symbol === data?.category
    );
    if (!selectedCoin) return;

    const quantityDecimal = selectedCoin.quantity_decimal || 0;
    const minOrderQty = selectedCoin.min_order_qty || 0;

    if (filter.label === "Order By Qty" && data?.quantity !== minOrderQty) {
      handleIncDec(data?.id, "", minOrderQty, quantityDecimal, minOrderQty);
    }
  }, [coinLists, filter.label]);
  return (
    <div className={styles.card}>
      {/* <div className={styles.card_header}>
        <MarketplaceCardSlider
          images={data?.productImages}
          imageUrl={imageUrl}
        />
        {data?.product_tag?.toLocaleLowerCase() === "new" && (
          <CardBadge>New</CardBadge>
        )}
      </div> */}
      <div className={styles.card_body}>
        {/* <div className={styles.card_headings}>
          <h3>{data?.title}</h3>
          <Button variant="without_bg_border" onClick={handleViewDetails}>
            <InfoIcon />
          </Button>
        </div>
        <p
          className={`${styles.desc_text} ${isDescriptionExpanded ? styles.expanded : ""
            }`}
          ref={descriptionRef}
        >
          Description: {data?.description}{" "}
          {isReadMoreVisible && (
            <Link to="#" onClick={handleToggleDescription}>
              {isDescriptionExpanded ? "Read less" : "Read more..."}
            </Link>
          )}
        </p> */}

        <div className={styles.header}>
          <div className={styles.headerleft}>
            <div className={styles.headerleft_imgsec}>
              <img src={tokenIcon} alt="token-icon" />
            </div>
            <div className={styles.headerleft_textsec}>
              <h4>{tokentitle}</h4>
              <h6>{tokensubInfo}</h6>
            </div>
          </div>
          {/* <div className={styles.headerright}>
            <img src={tokenCoin} alt="token-coin" />
          </div> */}
          {/* <div className={styles.headerright}>
            <img
              src={largeTokenIcon}
              alt={tokentitle}
              style={{ width: "80px", height: "80px", objectFit: "contain" }}
            />
          </div> */}
        </div>
        <div className={styles.card_priceSec}>
          <div className={styles.card_priceSec_leftsec}>
            <label>{pricelabel}</label>
            <h4 className="big">{getCurrentPremiumPrice()}</h4>
          </div>
          <div className={styles.card_priceSec_rightsec}>
            <label>{changelabel}</label>
            <h6>{changevalue}</h6>
          </div>
        </div>
        <div className={styles.qty}>
          <div className={styles.qtyfieldSec}>
            <div className={styles.qty_left}>
              <button
                onClick={() => {
                  if (filter.label === "Order By Qty") {
                    data?.quantity > minOrderQty &&
                      handleIncDec(
                        data?.id,
                        "-",
                        undefined,
                        quantityDecimal,
                        minOrderQty
                      );
                  } else {
                    const currentValue =
                      valueInput === "" ? 0 : Number(valueInput);
                    const unitPrice = getUnitPrice();
                    const currentQty = currentValue / unitPrice;

                    const newQty = Math.max(
                      currentQty - minOrderQty,
                      minOrderQty
                    );

                    handleIncDec(
                      data?.id,
                      "",
                      newQty,
                      quantityDecimal,
                      minOrderQty,
                      newQty * unitPrice
                    );
                    setValueInput((newQty * unitPrice).toFixed(2));
                  }
                }}
                disabled={data?.quantity <= minOrderQty}
              >
                <CirclesubtractionIcon />
              </button>
              <input
                value={
                  filter.label === "Order By Qty" ? data?.quantity : valueInput
                }
                onChange={(e) => handleInputChange(data?.id, e)}
                maxLength={10}
                placeholder={
                  filter.label === "Order By Qty"
                    ? "Enter Quantity"
                    : "Enter Value ($)"
                }
                onBlur={(e) => {
                  const val = e.target.value;
                  const selectedCoin = coinLists.find(
                    (coin) => coin.coin_symbol === data?.category
                  );
                  const maxDecimalPlaces = selectedCoin?.quantity_decimal || 2;
                  const minOrderQty = selectedCoin?.min_order_qty || 0;
                  const unitPrice = getUnitPrice();
                  const numericVal = Number(val);

                  if (filter.label === "Order By Qty") {
                    if (!val || isNaN(numericVal) || numericVal <= 0) {
                      handleIncDec(
                        data?.id,
                        "",
                        minOrderQty,
                        maxDecimalPlaces,
                        minOrderQty
                      );
                    } else {
                      const fixedQty = Math.max(
                        Number(numericVal.toFixed(maxDecimalPlaces)),
                        minOrderQty
                      );
                      handleIncDec(
                        data?.id,
                        "",
                        fixedQty,
                        maxDecimalPlaces,
                        minOrderQty
                      );
                    }
                  } else {
                    if (!val || isNaN(numericVal) || numericVal < 0) {
                      handleIncDec(
                        data?.id,
                        "",
                        minOrderQty,
                        maxDecimalPlaces,
                        minOrderQty,
                        minOrderQty * unitPrice
                      );
                      setValueInput((minOrderQty * unitPrice).toFixed(2));
                    } else if (numericVal === 0) {
                      handleIncDec(
                        data?.id,
                        "",
                        0,
                        maxDecimalPlaces,
                        minOrderQty,
                        0
                      );
                      setValueInput("0.00");
                    } else {
                      const qty = numericVal / unitPrice;
                      const fixedQty = Math.max(
                        Number(qty.toFixed(maxDecimalPlaces)),
                        minOrderQty
                      );
                      handleIncDec(
                        data?.id,
                        "",
                        fixedQty,
                        maxDecimalPlaces,
                        minOrderQty,
                        numericVal
                      );
                      setValueInput(numericVal.toFixed(2));
                    }
                  }
                }}
              />
              <button
                onClick={() => {
                  if (filter.label === "Order By Qty") {
                    handleIncDec(
                      data?.id,
                      "+",
                      undefined,
                      quantityDecimal,
                      minOrderQty
                    );
                  } else {
                    const currentValue =
                      valueInput === "" ? 0 : Number(valueInput);
                    const unitPrice = getUnitPrice();
                    const currentQty = currentValue / unitPrice;
                    const newQty = currentQty + minOrderQty;

                    handleIncDec(
                      data?.id,
                      "",
                      newQty,
                      quantityDecimal,
                      minOrderQty,
                      newQty * unitPrice
                    );
                    setValueInput((newQty * unitPrice).toFixed(2));
                  }
                }}
                disabled={data?.quantity >= getMaxQuantityFromProductPrices()}
              >
                <CircleadditionIcon />
              </button>
            </div>
            <Select
              isSearchable={false}
              options={timeOptions}
              value={filter}
              placeholder={<h5>Weekly</h5>}
              className={styles.select_time}
              onChange={(selected) => {
                setFilter(selected);
                const selectedCoin = coinLists.find(
                  (coin) => coin.coin_symbol === data?.category
                );
                const quantityDecimal = selectedCoin?.quantity_decimal || 0;
                const minOrderQty = selectedCoin?.min_order_qty || 0;

                if (selected.label === "Order By Value") {
                  handleIncDec(
                    data?.id,
                    "",
                    minOrderQty,
                    quantityDecimal,
                    minOrderQty,
                    minOrderQty * getUnitPrice()
                  );
                  setValueInput((minOrderQty * getUnitPrice()).toFixed(2));
                } else {
                  setValueInput("");
                  if (data?.quantity < minOrderQty) {
                    handleIncDec(
                      data?.id,
                      "",
                      minOrderQty,
                      quantityDecimal,
                      minOrderQty
                    );
                  }
                }
              }}
            // menuIsOpen={true}
            />
          </div>
          {/* <div className={styles.total}>
            <h5>
              {filter.label === "Order By Qty"
                ? `Total: $${getTotalPrice(data)}`
                : `Qty: ${toFix(data?.quantity || 0, quantityDecimal, false)}`}
            </h5>
          </div> */}

          <div className={styles.supply}>
            <label>Circulating Supply</label>
            <h6>
              {filter.label === "Order By Qty"
                ? `$${getTotalPrice(data)}`
                : `${toFix(data?.quantity || 0, quantityDecimal, false)}`}
            </h6>
          </div>
        </div>
        {/* <div className={styles.action_btn}>
          <Button
            variant={"secondary_btn"}
            fluid
            onClick={handleAddtoCartClick}
          // disabled={Number(data?.quantity) < Number(minOrderQty)}
          // disabled={
          //   filter.label === "Order By Value"
          //     ? Number(getQuantityFromValue(valueInput)) < Number(minOrderQty) || !valueInput || Number(valueInput) <= 0
          //     : Number(data?.quantity) < Number(minOrderQty)
          // }
          >
            Add to Cart
          </Button>
        </div> */}

        <div className={styles.btnRow}>

          <Button
            className="btnSize"
            variant="green_btn"
            fluid
            onClick={handleAddtoCartClick}
            // disabled={Number(data?.quantity) < Number(minOrderQty)}
            disabled={
              filter.label === "Order By Value"
                ? Number(getQuantityFromValue(valueInput)) < Number(minOrderQty) || !valueInput || Number(valueInput) <= 0
                : Number(data?.quantity) < Number(minOrderQty)
            }
          >
            Buy {tokenLabel}
          </Button>
          <Button
            fluid
            className="btnSize"
            variant="danger_filled"
            onClick={onClicksell}
          >
            Sell {tokenLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MarketPlaceCard;
